/**
 * Sectionly Image Tabs Web Component
 * Handles tab switching functionality with smooth transitions
 */
class SectionlyImageTabs {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error(`Sectionly Image Tabs: Container with ID "${containerId}" not found`);
      return;
    }
    
    this.tabs = this.container.querySelectorAll('.sectionly-image-tabs-tab');
    this.panels = this.container.querySelectorAll('.sectionly-image-tabs-panel');
    this.activeTabIndex = 0;
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.handleIconAlignment();
    this.setInitialState();
    this.initSwipers();
  }
  
  bindEvents() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTab(index);
      });
      
      // Add keyboard navigation
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.switchTab(index);
        }
        
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.navigateWithKeyboard(e.key, index);
        }
      });
    });
    

  }
  
  switchTab(newIndex) {
    if (newIndex === this.activeTabIndex || newIndex < 0 || newIndex >= this.tabs.length) {
      return;
    }
    
    // Remove active state from current tab and panel
    this.tabs[this.activeTabIndex].classList.remove('sectionly-image-tabs-tab--active');
    this.panels[this.activeTabIndex].classList.remove('sectionly-image-tabs-panel--active');
    
    // Add active state to new tab and panel
    this.tabs[newIndex].classList.add('sectionly-image-tabs-tab--active');
    this.panels[newIndex].classList.add('sectionly-image-tabs-panel--active');
    
    // Update active index
    this.activeTabIndex = newIndex;
    
    // Update ARIA attributes for accessibility
    this.updateAriaAttributes();
    
    // Trigger custom event
    this.dispatchTabChangeEvent(newIndex);
  }
  
  navigateWithKeyboard(direction, currentIndex) {
    let newIndex;
    
    if (direction === 'ArrowLeft') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
    } else {
      newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
    }
    
    this.tabs[newIndex].focus();
    this.switchTab(newIndex);
  }
  
  handleIconAlignment() {
    // Handle alignment for tabs without icons
    this.tabs.forEach(tab => {
      const icon = tab.querySelector('.sectionly-image-tabs-tab-icon');
      const text = tab.querySelector('.sectionly-image-tabs-tab-text');
      
      if (!icon && text) {
        text.classList.add('sectionly-image-tabs-tab-text--no-icon');
      }
    });
    
    // Handle alignment for descriptions without icons
    this.panels.forEach(panel => {
      const description = panel.querySelector('.sectionly-image-tabs-description');
      const icon = panel.querySelector('.sectionly-image-tabs-description-icon');
      
      if (description && !icon) {
        description.classList.add('sectionly-image-tabs-description--no-icon');
      }
    });
  }
  
  setInitialState() {
    // Set ARIA attributes for accessibility
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', index === this.activeTabIndex ? 'true' : 'false');
      tab.setAttribute('aria-controls', `panel-${index}`);
      tab.setAttribute('id', `tab-${index}`);
      tab.setAttribute('tabindex', index === this.activeTabIndex ? '0' : '-1');
    });
    
    this.panels.forEach((panel, index) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `tab-${index}`);
      panel.setAttribute('id', `panel-${index}`);
      panel.setAttribute('tabindex', '0');
    });
    
    // Set the tab container role
    const tabNav = this.container.querySelector('.sectionly-image-tabs-nav');
    if (tabNav) {
      tabNav.setAttribute('role', 'tablist');
    }
  }
  
  updateAriaAttributes() {
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('aria-selected', index === this.activeTabIndex ? 'true' : 'false');
      tab.setAttribute('tabindex', index === this.activeTabIndex ? '0' : '-1');
    });
  }
  
  dispatchTabChangeEvent(tabIndex) {
    const event = new CustomEvent('sectionlyImageTabsChange', {
      detail: {
        activeTabIndex: tabIndex,
        activeTab: this.tabs[tabIndex],
        activePanel: this.panels[tabIndex]
      },
      bubbles: true
    });
    
    this.container.dispatchEvent(event);
  }
  
  // Public methods for external control
  getActiveTabIndex() {
    return this.activeTabIndex;
  }
  
  setActiveTab(index) {
    this.switchTab(index);
  }
  
  getTabsCount() {
    return this.tabs.length;
  }
  

  
  // Method to refresh the component (useful for dynamic content)
  refresh() {
    this.destroySwipers(); // 清理旧的swiper实例
    this.tabs = this.container.querySelectorAll('.sectionly-image-tabs-tab');
    this.panels = this.container.querySelectorAll('.sectionly-image-tabs-panel');
    this.init();
  }
  
  // Initialize Swiper instances for image galleries
  initSwipers() {
    // 等待 Swiper 库加载
    if (typeof Swiper === 'undefined' && typeof SifyUtils !== 'undefined' && SifyUtils.Swiper) {
      // 使用本地 Swiper
      window.Swiper = SifyUtils.Swiper;
    }
    
    if (typeof Swiper === 'undefined') {
      // 再次尝试等待 Swiper 加载
      setTimeout(() => {
        this.initSwipers();
      }, 200);
      return;
    }
    
    // 获取容器ID中的section ID
    const sectionId = this.container.id.replace('sectionly-image-tabs-', '');
    this.swipers = this.swipers || {};
    
    // 查找所有swiper容器
    const swiperContainers = this.container.querySelectorAll(`[class*="sectionly-image-tabs-swiper-${sectionId}-"]`);
    
    swiperContainers.forEach((container) => {
      try {
        const containerClass = container.className;
        const uniqueIdMatch = containerClass.match(new RegExp(`sectionly-image-tabs-swiper-${sectionId}-(\\d+)`));
        
        if (uniqueIdMatch) {
          const uniqueId = uniqueIdMatch[1];
          const swiperId = `${sectionId}-${uniqueId}`;
          
          // 如果已经初始化过，先销毁
          if (this.swipers[swiperId]) {
            try {
              this.swipers[swiperId].destroy(true, true);
            } catch (e) {
              // 忽略销毁错误
            }
          }
          
          // 创建新的swiper实例，避免localStorage相关问题
          const swiperConfig = {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: true,
            observer: true,
            observeParents: true,
            navigation: {
              nextEl: `.swiper-button-next-${sectionId}-${uniqueId}`,
              prevEl: `.swiper-button-prev-${sectionId}-${uniqueId}`,
            },
            // 避免localStorage错误
            watchSlidesProgress: false,
            watchSlidesVisibility: false,
          };
          
          this.swipers[swiperId] = new Swiper(`.sectionly-image-tabs-swiper-${sectionId}-${uniqueId}`, swiperConfig);
        }
      } catch (error) {
        console.warn('Swiper initialization failed:', error);
      }
    });
  }
  
  // 销毁所有swiper实例
  destroySwipers() {
    if (this.swipers) {
      Object.values(this.swipers).forEach(swiper => {
        try {
          if (swiper && typeof swiper.destroy === 'function') {
            swiper.destroy(true, true);
          }
        } catch (e) {
          // 忽略销毁错误
        }
      });
      this.swipers = {};
    }
  }
}

// Auto-initialize for theme editor compatibility
document.addEventListener('DOMContentLoaded', function() {
  // Initialize instances storage
  window.sectionlyImageTabsInstances = window.sectionlyImageTabsInstances || {};
  
  // Find all sectionly image tabs instances
  const tabContainers = document.querySelectorAll('[id^="sectionly-image-tabs-"]');
  
  tabContainers.forEach(container => {
    if (!container.dataset.initialized) {
      const instance = new SectionlyImageTabs(container.id);
      window.sectionlyImageTabsInstances[container.id] = instance;
      container.dataset.initialized = 'true';
    }
  });
});

// Handle Shopify theme editor events
document.addEventListener('shopify:section:load', function(event) {
  const tabContainer = event.target.querySelector('[id^="sectionly-image-tabs-"]');
  if (tabContainer) {
    const instance = new SectionlyImageTabs(tabContainer.id);
    window.sectionlyImageTabsInstances[tabContainer.id] = instance;
    tabContainer.dataset.initialized = 'true';
  }
});

document.addEventListener('shopify:section:unload', function(event) {
  const tabContainer = event.target.querySelector('[id^="sectionly-image-tabs-"]');
  if (tabContainer && window.sectionlyImageTabsInstances[tabContainer.id]) {
    // 清理swiper实例
    window.sectionlyImageTabsInstances[tabContainer.id].destroySwipers();
    delete window.sectionlyImageTabsInstances[tabContainer.id];
    tabContainer.dataset.initialized = '';
  }
});

document.addEventListener('shopify:section:reorder', function(event) {
  const tabContainer = event.target.querySelector('[id^="sectionly-image-tabs-"]');
  if (tabContainer && tabContainer.dataset.initialized) {
    if (window.sectionlyImageTabsInstances[tabContainer.id]) {
      window.sectionlyImageTabsInstances[tabContainer.id].refresh();
    } else {
      const instance = new SectionlyImageTabs(tabContainer.id);
      window.sectionlyImageTabsInstances[tabContainer.id] = instance;
    }
  }
});

document.addEventListener('shopify:block:select', function(event) {
  const tabContainer = event.target.closest('[id^="sectionly-image-tabs-"]');
  if (tabContainer) {
    let instance = window.sectionlyImageTabsInstances[tabContainer.id];
    if (!instance) {
      instance = new SectionlyImageTabs(tabContainer.id);
      window.sectionlyImageTabsInstances[tabContainer.id] = instance;
    }
    const blockIndex = Array.from(event.target.parentNode.children).indexOf(event.target);
    instance.setActiveTab(blockIndex);
  }
});

// Export for global access
window.SectionlyImageTabs = SectionlyImageTabs; 